export const COMMUNITY_TOUR_VERSION = 1;
export const COMMUNITY_TOUR_STEP_COUNT = 7;

export interface CommunityTourState {
  version: number;
  status: 'pending' | 'completed' | 'skipped';
  step: number;
}

export function communityTourKey(userId: string): string {
  return `vent.community-tour.v${COMMUNITY_TOUR_VERSION}.${userId}`;
}

export function parseCommunityTourState(raw: string | null): CommunityTourState | null {
  if (!raw) return null;
  try {
    const state = JSON.parse(raw);
    if (state.version !== COMMUNITY_TOUR_VERSION ||
      !['pending', 'completed', 'skipped'].includes(state.status) ||
      !Number.isInteger(state.step) || state.step < 0 || state.step >= COMMUNITY_TOUR_STEP_COUNT) return null;
    return state;
  } catch {
    return null;
  }
}

export function shouldOfferCommunityTour(onboardingComplete: boolean, state: CommunityTourState | null): boolean {
  return onboardingComplete && (!state || state.status === 'pending');
}

export function pendingCommunityTour(step = 0): CommunityTourState {
  return { version: COMMUNITY_TOUR_VERSION, status: 'pending', step };
}

export interface TourStorageAdapter {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
}

/** Native failures must fall back for reads as well as writes. */
export function withTourStorageFallback(primary: TourStorageAdapter, fallback: TourStorageAdapter): TourStorageAdapter {
  return {
    async getItem(key) {
      try {
        return (await primary.getItem(key)) ?? await fallback.getItem(key);
      } catch {
        return fallback.getItem(key);
      }
    },
    async setItem(key, value) {
      // Mirror locally so a later native-read failure retains completion/progress.
      await fallback.setItem(key, value);
      try { await primary.setItem(key, value); } catch { /* local copy is durable */ }
    },
  };
}